/**
 * 跨标签页互斥 —— W1-3。
 *
 * 为什么需要它：`singleFlight` 只管得住**本页面**，而后端的 refresh 令牌是**一次性**的
 * (`RefreshTokenStore` 用掉即删键)。于是 Web 上开两个标签时：
 *
 *   1. 标签 A 的 access 过期 → 刷新 → 新令牌写进 localStorage；
 *   2. 标签 B 手里那张 refresh **已经作废**，它却毫不知情；
 *   3. B 拿它去刷 → 失败 → 把用户踢去登录。
 *
 * ⚠ 这在壳里永远碰不到(只有一个 WebView)，所以到 Web 优先之前一直没被发现。
 * ⚠ 光有锁不够：等到锁之后还必须**重读**令牌(见 refresh.ts)，否则拿的还是那张废票。
 */

/** 拿着名为 `name` 的锁执行 `fn`。 */
export type WithLock = <T>(name: string, fn: () => Promise<T>) => Promise<T>

/** 不做跨标签协调，直接执行。默认退路 —— 行为与引入本模块之前完全一致。 */
export const withoutCrossTabLock: WithLock = (_name, fn) => fn()

/**
 * Web Locks 实现；拿不到 API 就退回 `withoutCrossTabLock`。
 *
 * ⚠ `navigator.locks` 要求**安全上下文**：https 与 localhost 有，
 *   http 的内网 IP(常见的测试机地址)没有。旧 Safari 与部分壳环境也没有。
 *   所以降级不是可选项，是必然会走到的分支 —— 降级后只是回到「多标签偶发重登」，
 *   不比今天差，**绝不能因此抛异常**。
 */
/** Web Locks 里我们真正用到的那一点点。用结构类型而不是 `LockManager`，测试才好造假的。 */
export interface LockRequester {
  request<T>(name: string, fn: () => Promise<T>): Promise<T>
}

export function createWebLocksGuard(
  scope: { locks?: LockRequester } | undefined = globalThis.navigator,
): WithLock {
  const locks = scope?.locks
  if (typeof locks?.request !== 'function') return withoutCrossTabLock

  return async <T>(name: string, fn: () => Promise<T>): Promise<T> => {
    let started = false
    try {
      return await locks.request(name, async () => {
        started = true
        return fn()
      })
    } catch (error) {
      /*
       * ⚠⚠ 必须分清「没拿到锁」和「fn 自己抛了」：
       *   fn 抛了还去重跑一遍 = 刷新请求发两次，第二次拿的是**已经被消费掉**的
       *   refresh 令牌 —— 那正是本模块要修的那个 bug，别在修它的路上又造一个。
       */
      if (started) throw error
      return fn()
    }
  }
}
